<template>
  <div id="samples">
    <div id="view-sub-nav">
      <template v-if="regionOptions.length > 1">
        <span class="mr-3 text-body-2">Region</span>
        <v-select
          v-model="region"
          :items="regionOptions"
          hide-details
          density="compact"
          style="max-width: 80px"
          class="mr-5"
        ></v-select>
      </template>
      <!-- <v-switch -->
      <v-radio-group inline hide-details v-model="listFilter" class="flex-grow-0" @update:model-value="bindSamples">
        <v-radio label="Awaiting Dispatch" value="production" />
        <v-radio label="Follow Up" value="follow" />
        <v-radio label="Dispatched" value="dispatched" />
      </v-radio-group>
      <v-text-field
        v-model="search"
        label="Search email or order #"
        append-icon="mdi-magnify"
        single-line
        hide-details
        density="compact"
        :clearable="true"
        class="ml-5"
        style="max-width: 260px"
      ></v-text-field>
      <v-spacer></v-spacer>
      <div v-if="selected.length > 0" class="d-flex align-center">
        <span class="text-body-2">{{ selected.length }} Items Selected</span>
        <v-btn
          v-if="listFilter !== 'follow'"
          class="ml-3"
          variant="outlined"
          color="primary"
          :disabled="selected.length == 0 || listFilter !== 'production'"
          @click="markAsDispatched"
          >Mark As Dispatched</v-btn
        >
        <v-btn
          v-if="listFilter === 'follow'"
          class="ml-3"
          variant="outlined"
          color="primary"
          :disabled="selected.length == 0"
          @click="followUpDialog = true"
          >Send Follow Up</v-btn
        >
        <v-btn
          v-if="listFilter !== 'follow'"
          class="ml-3"
          variant="outlined"
          color="primary"
          :disabled="selected.length == 0"
          @click="
            () => {
              region === 'US' ? csvUS() : region === 'EU' ? csvEU() : csv()
            }
          "
          >Download CSV</v-btn
        >
        <v-btn
          v-if="listFilter !== 'follow'"
          class="ml-3"
          variant="outlined"
          color="primary"
          @click="updateMissingCustomers"
          >Update Customers</v-btn
        >
        <v-btn
          v-if="
            ['sho@plykea.com', 'kath@plykea.com', 'adam@plykea.com', 'laura@plykea.com'].includes(
              $store.state.user.email
            )
          "
          class="ml-3"
          variant="outlined"
          color="error"
          :disabled="selected.length == 0"
          @click="resetDispatchDialog = true"
          >Reset Dispatch Count</v-btn
        >
      </div>
      <v-btn
        v-else-if="listFilter === 'production'"
        class="ml-3"
        variant="outlined"
        color="primary"
        @click="addDialog = true"
        >Add Sample Pack</v-btn
      >
    </div>
    <v-data-table
      v-model="selected"
      :headers="headers"
      :items="filteredSamples"
      :loading="loading"
      :show-select="listFilter !== 'dispatched'"
      item-value="id"
      return-object
      :sort-by="[{ key: 'dateCreated', order: 'desc' }]"
      :hide-default-footer="listFilter === 'production'"
      :items-per-page="listFilter !== 'production' ? 10 : -1"
    >
      <!-- Custom header checkbox slot for select all -->
      <template v-slot:header.data-table-select="{ allSelected, someSelected }">
        <v-checkbox
          :model-value="allSelected"
          :indeterminate="someSelected && !allSelected"
          @update:model-value="handleSelectAll"
          hide-details
          class="ma-0 pa-0"
        ></v-checkbox>
      </template>

      <!-- Custom checkbox slot to disable selection -->
      <!-- V3: isSelected is a predicate over internal items, not a boolean, and select is now toggleSelect -->
      <template v-slot:item.data-table-select="{ item, internalItem, isSelected, toggleSelect }">
        <v-checkbox
          :model-value="isSelected(internalItem)"
          :disabled="(listFilter === 'follow' && item.allowFollowUp === false) || item.balanceOutstanding === true"
          @update:model-value="toggleSelect(internalItem)"
          hide-details
          class="ma-0 pa-0"
        ></v-checkbox>
      </template>

      <!-- Headers -->
      <template v-slot:header.date>
        {{ listFilter === 'production' ? 'Ordered' : 'Dispatched' }}
      </template>

      <template v-slot:item.orderNumber="{ item }">
        {{ item.orderNumber || item.xeroInvoiceNumber || 'FOC' }}
      </template>

      <template v-slot:item.daysSinceDispatch="{ item }"> {{ item.daysSinceDispatch || 0 }} days </template>

      <!-- Items -->
      <template v-slot:item.date="{ item }">
        {{ listFilter !== 'production' ? $utils.convertDate(item.dispatchDate) : $utils.convertDate(item.dateCreated) }}
      </template>
      <template v-slot:item.materials="{ item }">
        {{ item.materials || 'Mixed (OLD)' }}
      </template>

      <!-- Admin-only delete action for unpaid (0 payment) samples that can't be selected -->
      <template v-slot:item.actions="{ item }">
        <v-tooltip v-if="item.balanceOutstanding === true" location="top" :open-delay="500">
          <template v-slot:activator="{ props }">
            <v-icon size="small" color="error" v-bind="props" @click="openDeleteDialog(item)">mdi-delete</v-icon>
          </template>
          <span>Delete unpaid sample order</span>
        </v-tooltip>
      </template>
    </v-data-table>

    <v-dialog v-model="addDialog" width="800" persistent>
      <component :is="formComponent" @cancel="addDialog = false" @add="addSamplePack"></component>
    </v-dialog>

    <v-dialog v-model="followUpDialog" width="600">
      <v-card>
        <v-card-title class="text-h5">Send Follow Up Emails</v-card-title>
        <v-card-text>
          <p class="mb-4">Are you sure you want to send follow-up emails to the following samples?</p>
          <v-list density="compact">
            <v-list-item v-for="sample in selected" :key="sample.id">
              <v-list-item-title>
                Order #{{ sample.orderNumber || sample.xeroInvoiceNumber || 'FOC' }}
              </v-list-item-title>
              <v-list-item-subtitle>
                {{ sample.customerContactDetails.email }} - {{ sample.daysSinceDispatch || 0 }} days since dispatch
              </v-list-item-subtitle>
            </v-list-item>
          </v-list>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="followUpDialog = false">Cancel</v-btn>
          <v-btn color="primary" @click="sendFollowUp">Confirm</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="resetDispatchDialog" width="800">
      <v-card>
        <v-card-title class="text-h5 bg-error text-white">Reset Dispatch Count</v-card-title>
        <v-card-text class="mt-4">
          <p class="mb-4 font-weight-bold">
            WARNING: This will reset the dispatch count to 0 for the following {{ selected.length }} sample(s):
          </p>
          <v-table density="compact">
            <template v-slot:default>
              <thead>
                <tr>
                  <th>Order #</th>
                  <th>Customer Email</th>
                  <th>Dispatched</th>
                  <th>Xero Contact ID</th>
                  <th>Current Dispatch Count</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="sample in selected" :key="sample.id">
                  <td>{{ sample.orderNumber || sample.xeroInvoiceNumber || 'FOC' }}</td>
                  <td>{{ sample.customerContactDetails.email }}</td>
                  <td>
                    <v-chip size="small" :color="sample.dispatched ? 'success' : 'warning'">
                      {{ sample.dispatched ? 'Yes' : 'No' }}
                    </v-chip>
                  </td>
                  <td>{{ sample.xeroContactID || 'N/A' }}</td>
                  <td>
                    <v-chip size="small" :color="sample.numOfMarkedAsDispatched > 0 ? 'error' : 'default'">
                      {{ sample.numOfMarkedAsDispatched || 0 }}
                    </v-chip>
                  </td>
                </tr>
              </tbody>
            </template>
          </v-table>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="resetDispatchDialog = false">Cancel</v-btn>
          <v-btn color="error" @click="resetDispatchCount">Confirm Reset</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="deleteDialog" width="640" persistent>
      <v-card v-if="orderToDelete">
        <v-card-title class="text-h5 bg-error text-white">Delete Sample Order</v-card-title>
        <v-card-text class="mt-4">
          <v-alert density="compact" text type="warning" class="mb-4">
            This order has <strong>{{ orderToDelete.numPayments || 0 }} payment(s)</strong> and an outstanding balance.
            Deleting it is permanent and cannot be undone.
          </v-alert>

          <p class="mb-2 font-weight-bold">You are about to permanently delete:</p>
          <v-table density="compact" class="mb-4">
            <template v-slot:default>
              <tbody>
                <tr>
                  <td class="font-weight-medium">Order #</td>
                  <td>{{ orderToDelete.orderNumber || orderToDelete.xeroInvoiceNumber || 'FOC' }}</td>
                </tr>
                <tr>
                  <td class="font-weight-medium">Payments</td>
                  <td>{{ orderToDelete.numPayments || 0 }}</td>
                </tr>
                <tr>
                  <td class="font-weight-medium">Name</td>
                  <td>
                    {{ orderToDelete.customerContactDetails.firstName }}
                    {{ orderToDelete.customerContactDetails.lastName }}
                  </td>
                </tr>
                <tr>
                  <td class="font-weight-medium">Email</td>
                  <td>{{ orderToDelete.customerContactDetails.email }}</td>
                </tr>
                <tr>
                  <td class="font-weight-medium">Address</td>
                  <td>{{ formatAddress(orderToDelete) }}</td>
                </tr>
              </tbody>
            </template>
          </v-table>

          <div v-if="checkingRelated" class="d-flex align-center text-grey-darken-1">
            <v-progress-circular indeterminate size="16" width="2" class="mr-2" color="primary" />
            Checking for other sample orders with this email…
          </div>
          <template v-else>
            <v-alert v-if="relatedSamples.length > 0" density="compact" text type="info" class="mb-0">
              <p class="mb-2 font-weight-bold">
                {{ relatedSamples.length }} other sample order(s) share this email ({{
                  orderToDelete.customerContactDetails.email
                }}):
              </p>
              <v-list density="compact" color="transparent" class="py-0">
                <!-- v-list-item-content is removed in V3: VListItem lays its own children out. -->
                <v-list-item v-for="s in relatedSamples" :key="s.id" class="px-0 py-0" style="min-height: 28px">
                  <v-list-item-title class="text-blue-darken-4">
                    Order #{{ s.orderNumber || s.xeroInvoiceNumber || 'FOC' }} — {{ s.numPayments || 0 }} payment(s),
                    {{ s.dispatched ? 'dispatched' : 'awaiting dispatch' }},
                    {{ s.balanceOutstanding ? 'balance outstanding' : 'paid/FOC' }}
                  </v-list-item-title>
                </v-list-item>
              </v-list>
              <span class="text-caption text-blue-darken-4"
                >These will NOT be deleted — only the order above is removed.</span
              >
            </v-alert>
            <p v-else class="text-grey-darken-1 mb-0">No other sample orders found with this email.</p>
          </template>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="closeDeleteDialog">Cancel</v-btn>
          <v-btn color="error" @click="confirmDelete">Confirm Delete</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-overlay
      :model-value="true"
      v-if="submitting"
      class="align-center justify-center text-center"
      opacity=".95"
      scrim="primary"
    >
      <v-progress-circular class="mt-4" :size="50" :width="3" color="accent" indeterminate></v-progress-circular>
    </v-overlay>
  </div>
</template>

<script>
import filesaver from 'file-saver'
import { db, functions } from '@/js/db'
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  increment,
  query,
  updateDoc,
  where,
  writeBatch
} from 'firebase/firestore'
import { httpsCallable } from 'firebase/functions'
import ukForm from '@/components/samples/uk-form'
import euForm from '@/components/samples/eu-form'
import usForm from '@/components/samples/us-form'

export default {
  name: 'Samples',
  components: { ukForm, euForm, usForm },
  data() {
    return {
      listFilter: 'production',
      addDialog: false,
      followUpDialog: false,
      resetDispatchDialog: false,
      deleteDialog: false,
      orderToDelete: null,
      relatedSamples: [],
      checkingRelated: false,
      region: this.$store.state.user.permissions.uk ? 'UK' : 'US',
      loading: true,
      selected: [],
      samples: [],
      search: '',

      submitting: false
    }
  },
  created() {
    this.bindSamples()
  },
  watch: {
    region() {
      this.selected = []
      this.search = ''
    },
    listFilter() {
      this.selected = []
      this.search = ''
    },
    search() {
      this.selected = []
    }
  },
  methods: {
    async bindSamples() {
      this.loading = true
      let q = query(
        collection(db, 'orders'),
        where('type', '==', 'sample-pack'),
        where('dispatched', '==', this.listFilter !== 'production')
      )
      if (this.listFilter !== 'production') {
        q = query(q, where('balanceOutstanding', '==', false))
      }
      await this.$live('samples', q)

      for (const sample of this.samples) {
        if (!sample?.numOfMarkedAsDispatched && sample.dispatched == false) {
          // If the sample does not have numOfMarkedAsDispatched, set it to 0
          sample.numOfMarkedAsDispatched = 0
        }
      }

      this.loading = false
    },
    matchesSearch(sample) {
      const q = (this.search || '').trim().toLowerCase()
      if (!q) return true

      const email = (sample.customerContactDetails && sample.customerContactDetails.email) || ''
      if (email.toLowerCase().includes(q)) return true

      const digits = q.replace(/^#/, '').replace(/^inv-/, '')
      if (!/^\d+$/.test(digits)) return false

      return [sample.orderNumber, sample.xeroInvoiceNumber]
        .filter((value) => value !== null && value !== undefined)
        .some((value) => String(value).replace(/\D/g, '').includes(digits))
    },
    toggleAll() {
      if (this.selected.length) this.selected = []
      else this.selected = this.filteredSamples.slice()
    },
    handleSelectAll(selected) {
      if (selected) {
        if (this.listFilter === 'follow') {
          this.selected = this.filteredSamples.filter((item) => item.allowFollowUp !== false)
        } else {
          this.selected = this.filteredSamples.filter((item) => item.balanceOutstanding !== true)
        }
      } else {
        this.selected = []
      }
    },
    changeSort(column) {
      if (this.pagination.sortBy === column) {
        this.pagination.descending = !this.pagination.descending
      } else {
        this.pagination.sortBy = column
        this.pagination.descending = false
      }
    },
    constructUkSampleString(sample, material) {
      return `${sample.customerContactDetails.firstName} ${sample.customerContactDetails.lastName};${
        sample.customerContactDetails?.deliveryAddressLine2
          ? sample.customerContactDetails.deliveryAddressLine1 +
            ', ' +
            sample.customerContactDetails.deliveryAddressLine2
          : sample.customerContactDetails.deliveryAddressLine1
      };${sample.customerContactDetails.deliveryCity};${sample.customerContactDetails.deliveryPostcode};${
        sample.customerContactDetails.deliveryCountry
      };${material};\n`
    },
    csv() {
      let s =
        'Shipping Address - Full Name;Shipping address - line 1;Shipping address - City;Shipping address - Postcode;Shipping address - Country;Materials - Product Name;\n'

      for (const sample of this.selected) {
        if (sample.materials === 'both') {
          s += this.constructUkSampleString(sample, 'laminate;')
          s += this.constructUkSampleString(sample, 'veneer;')
        } else {
          s += this.constructUkSampleString(sample, sample.materials || 'mixed;')
        }
      }
      var blob = new Blob([s], { type: 'text/csv' })
      filesaver.saveAs(blob, 'samples.csv')
    },
    constructEuSampleString(sample, material) {
      return `${sample.customerContactDetails.firstName} ${sample.customerContactDetails.lastName};${
        sample.customerContactDetails?.deliveryAddressLine2
          ? sample.customerContactDetails.deliveryAddressLine1 +
            ', ' +
            sample.customerContactDetails.deliveryAddressLine2
          : sample.customerContactDetails.deliveryAddressLine1
      };${sample.customerContactDetails.deliveryCity};${sample.customerContactDetails.deliveryPostcode};${
        sample.customerContactDetails.deliveryCountry
      };${material};\n`
    },
    csvEU() {
      let s =
        'Shipping Address - Full Name;Shipping address - line 1;Shipping address - City;Shipping address - Postcode;Shipping address - Country;Materials - Product Name;\n'
      for (const sample of this.selected) {
        if (sample.materials === 'both') {
          s += this.constructEuSampleString(sample, 'laminate;')
          s += this.constructEuSampleString(sample, 'veneer;')
        } else {
          s += this.constructEuSampleString(sample, sample.materials || 'mixed;')
        }
      }
      filesaver.saveAs(new Blob([s], { type: 'text/csv' }), 'samples-eu.csv')
    },
    csvUS() {
      var s =
        'Order ID (required),Order Date,Order Value,Requested Service,Ship To - Name,Ship To - Company,Ship To - Address 1,Ship To - Address 2,Ship To - Address 3,Ship To - State/Province,Ship To - City,Ship To - Postal Code,Ship To - Country,Ship To - Phone,Ship To - Email,Total Weight in Oz,Dimensions - Length,Dimensions - Width,Dimensions - Height,Notes - From Customer,Notes - Internal,Gift Wrap?,Gift Message\n'
      for (var i = this.selected.length - 1; i >= 0; i--) {
        let c = this.selected[i]

        let address2 = c.customerContactDetails.deliveryAddressLine2
          ? c.customerContactDetails.deliveryAddressLine2
          : ''
        s +=
          c.id +
          ',' +
          this.$moment(c.dateCreated.toDate()).format('M/D/YYYY') +
          ',' +
          5.0 +
          ',' +
          'Standard Shipping' +
          ',' +
          c.customerContactDetails.firstName +
          ' ' +
          c.customerContactDetails.lastName +
          ',,' +
          c.customerContactDetails.deliveryAddressLine1 +
          ',' +
          address2 +
          ',,' +
          c.customerContactDetails.deliveryState +
          ',' +
          c.customerContactDetails.deliveryCity +
          ',' +
          c.customerContactDetails.deliveryPostcode +
          ',' +
          'US' +
          ',,' +
          c.customerContactDetails.email +
          ',' +
          '32' +
          ',0.0,0.0,0.0,,,FALSE,' +
          '\n'
      }
      var blob = new Blob([s], { type: 'text/csv' })
      filesaver.saveAs(blob, 'samples.csv')
    },
    async updateMissingCustomers() {
      this.submitting = true

      try {
        const customerArray = []
        for (const order of this.selected) {
          const c = { ...order.customerContactDetails }
          c.region = order.region
          customerArray.push(c)
        }
        const updateMissingCustomerFunc = httpsCallable(functions, 'updateMissingCustomers')
        await updateMissingCustomerFunc(customerArray)
      } catch (error) {
        this.submitting = false
        this.selected = []
      }

      this.submitting = false
      this.selected = []
      this.$store.commit('openSnackbar', {
        snackbarText: 'Customers Updated'
      })
    },
    async markAsDispatched() {
      this.submitting = true

      try {
        const validOrders = this.selected.filter((order) => (order.numOfMarkedAsDispatched || 0) === 0)
        const orderIdArray = validOrders.map((order) => order.id)

        await Promise.all(
          validOrders.map((order) => {
            return updateDoc(doc(db, 'orders', order.id), {
              numOfMarkedAsDispatched: increment(1)
            })
          })
        )

        // Call cloud function - it will handle ALL updates including dispatch fields
        const invoiceSamplePacksFunc = httpsCallable(functions, 'invoiceSamplePacksBulk')
        await invoiceSamplePacksFunc(orderIdArray)

        this.submitting = false
        this.selected = []
        this.$store.commit('openSnackbar', {
          snackbarText: 'Samples dispatched'
        })
      } catch (error) {
        console.error('Error dispatching samples:', error)
        this.submitting = false
        this.selected = []
        this.$store.commit('openSnackbar', {
          snackbarText: 'Error dispatching samples. Please try again.'
        })
      }
    },
    async _buildSampleOrder(form) {
      const currencyByRegion = { UK: 'GBP', EU: 'EUR', US: 'USD' }
      // Samples are free of charge in every region — no price, no payment.
      const isEu = this.region === 'EU'

      let o = {
        customerContactDetails: {
          firstName: form.firstName,
          lastName: form.lastName,
          email: form.email,
          deliveryAddressLine1: form.addressLine1,
          deliveryAddressLine2: form.addressLine2,
          deliveryCity: form.city,
          deliveryPostcode: form.postcode,
          deliveryCountry: form.country,
          billingSameAsDelivery: true
        },
        materials: this.region === 'US' ? 'Mixed (OLD)' : form.materials,
        dateCreated: this.$utils.tsNow(),
        type: 'sample-pack',
        region: this.region.toLowerCase(),
        currency: currencyByRegion[this.region],
        total: 0,
        balanceOutstanding: false,
        numPayments: 0,
        payments: [],
        dispatched: false,
        numOfMarkedAsDispatched: 0,
        followUpSent: false
      }

      // EU samples are flagged FOC so they route through the no-invoice dispatch path.
      if (isEu) {
        o.foc = true
      }

      if (this.region === 'US') {
        o.customerContactDetails.deliveryState = form.state
      }

      return o
    },
    async addSamplePack(form) {
      this.addDialog = false
      const o = await this._buildSampleOrder(form)
      await addDoc(collection(db, 'orders'), o)
      this.$store.commit('openSnackbar', { snackbarText: 'Sample Pack Added' })
    },
    async sendFollowUp() {
      this.followUpDialog = false
      this.submitting = true

      try {
        // Update followUpSent to true for selected samples
        await Promise.all(
          this.selected.map((sample) => {
            return updateDoc(doc(db, 'orders', sample.id), {
              followUpSent: true
            })
          })
        )

        // Send follow-up emails via Firebase function
        const orderIdArray = this.selected.map((sample) => sample.id)
        const sampleFollowUpFunc = httpsCallable(functions, 'sampleFollowUpBulk')
        await sampleFollowUpFunc(orderIdArray)

        this.submitting = false
        const sentCount = this.selected.length
        this.selected = []
        this.$store.commit('openSnackbar', {
          snackbarText: `Follow-up emails sent to ${sentCount} customers`
        })
      } catch (error) {
        console.error('Error sending follow-up emails:', error)
        this.submitting = false
        this.$store.commit('openSnackbar', {
          snackbarText: 'Error sending follow-up emails'
        })
      }
    },
    formatAddress(sample) {
      const c = sample.customerContactDetails || {}
      return [
        c.deliveryAddressLine1,
        c.deliveryAddressLine2,
        c.deliveryCity,
        c.deliveryState,
        c.deliveryPostcode,
        c.deliveryCountry
      ]
        .filter(Boolean)
        .join(', ')
    },
    async openDeleteDialog(item) {
      // Guard: only admins may delete, and only unpaid (0 payment) orders.
      if (!this.$store.state.user.permissions.admin || item.balanceOutstanding !== true) return
      this.orderToDelete = item
      this.relatedSamples = []
      this.deleteDialog = true
      await this.findRelatedSamples(item)
    },
    async findRelatedSamples(item) {
      const email = item.customerContactDetails && item.customerContactDetails.email
      if (!email) {
        this.relatedSamples = []
        return
      }
      this.checkingRelated = true
      try {
        // Single-field query (no composite index needed); filter to sample-packs client-side.
        const snap = await getDocs(query(collection(db, 'orders'), where('customerContactDetails.email', '==', email)))
        this.relatedSamples = snap.docs
          .map((orderDoc) => ({ id: orderDoc.id, ...orderDoc.data() }))
          .filter((o) => o.id !== item.id && o.type === 'sample-pack')
      } catch (error) {
        console.error('Error checking for related sample orders:', error)
        this.relatedSamples = []
      }
      this.checkingRelated = false
    },
    closeDeleteDialog() {
      this.deleteDialog = false
      this.orderToDelete = null
      this.relatedSamples = []
      this.checkingRelated = false
    },
    async confirmDelete() {
      if (!this.orderToDelete) return
      // Re-check the guard at commit time in case state changed while the dialog was open.
      if (!this.$store.state.user.permissions.admin || this.orderToDelete.balanceOutstanding !== true) {
        this.closeDeleteDialog()
        return
      }
      const orderId = this.orderToDelete.id
      this.deleteDialog = false
      this.submitting = true
      try {
        await deleteDoc(doc(db, 'orders', orderId))
        this.$store.commit('openSnackbar', { snackbarText: 'Sample order deleted' })
      } catch (error) {
        console.error('Error deleting sample order:', error)
        this.$store.commit('openSnackbar', { snackbarText: 'Error deleting sample order. Please try again.' })
      }
      this.submitting = false
      this.orderToDelete = null
      this.relatedSamples = []
    },
    async resetDispatchCount() {
      this.resetDispatchDialog = false
      this.submitting = true

      try {
        // Create a batch update to reset numOfMarkedAsDispatched to 0
        const batch = writeBatch(db)

        this.selected.forEach((sample) => {
          const sampleRef = doc(db, 'orders', sample.id)
          batch.update(sampleRef, {
            numOfMarkedAsDispatched: 0
          })
        })

        // Commit the batch update
        await batch.commit()

        this.submitting = false
        const resetCount = this.selected.length
        this.selected = []

        this.$store.commit('openSnackbar', {
          snackbarText: `Dispatch count reset to 0 for ${resetCount} sample(s)`
        })

        // Refresh the samples list to show updated data
        await this.bindSamples()
      } catch (error) {
        console.error('Error resetting dispatch count:', error)
        this.submitting = false
        this.$store.commit('openSnackbar', {
          snackbarText: 'Error resetting dispatch count. Please try again.'
        })
      }
    }
  },
  computed: {
    headers() {
      const baseHeaders = [
        {
          title: 'Date',
          key: 'date'
        },
        {
          title: 'Order #',
          key: 'orderNumber',
          sortable: true
        },
        {
          title: 'Email',
          key: 'customerContactDetails.email',
          sortable: false
        },
        {
          title: 'Materials',
          key: 'materials',
          sortable: false
        },
        {
          title: 'City',
          key: 'customerContactDetails.deliveryCity',
          sortable: false
        },
        {
          title: 'Postcode',
          key: 'customerContactDetails.deliveryPostcode',
          sortable: false
        },
        {
          title: 'Country',
          key: 'customerContactDetails.deliveryCountry',
          sortable: false
        }
      ]

      // Add elapsed days column when on follow up filter
      if (this.listFilter === 'follow') {
        baseHeaders.splice(1, 0, {
          title: 'Days Since Dispatch',
          key: 'daysSinceDispatch',
          sortable: true
        })
      }

      // Admin-only actions column (delete icon for unpaid samples)
      if (this.$store.state.user.permissions.admin) {
        baseHeaders.push({
          title: '',
          key: 'actions',
          sortable: false,
          align: 'end'
        })
      }

      return baseHeaders
    },
    regionOptions() {
      const opts = []
      if (this.$store.state.user.permissions.uk) {
        opts.push({ text: 'UK', value: 'UK' })
        opts.push({ text: 'EU', value: 'EU' })
      }
      if (this.$store.state.user.permissions.us) {
        opts.push({ text: 'US', value: 'US' })
      }
      return opts
    },
    formComponent() {
      if (this.region === 'UK') return 'ukForm'
      if (this.region === 'EU') return 'euForm'
      return 'usForm'
    },
    filteredSamples() {
      const regionSamples = this.samples.filter((sample) => {
        const sampleRegion = (sample.region || '').toLowerCase()
        if (sampleRegion) {
          return sampleRegion === this.region.toLowerCase()
        }
        // Legacy samples predate the `region` field — fall back to country-based bucketing.
        const country = sample.customerContactDetails && sample.customerContactDetails.deliveryCountry
        if (this.region === 'US') return country === 'US'
        if (this.region === 'UK') return country === 'GB'
        return country !== 'US' && country !== 'GB'
      })
      let filtered = []
      if (this.listFilter === 'follow') {
        filtered = regionSamples.filter((sample) => sample.dispatched && sample.followUpSent === false)
        // Calculate days since dispatch for each sample
        filtered.forEach((sample) => {
          if (sample.dispatchDate) {
            const dispatchDate = sample.dispatchDate.toDate()
            const today = new Date()
            const diffTime = Math.abs(today - dispatchDate)
            sample.daysSinceDispatch = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
            // Set allowFollowUp to true if it's been more than 7 days since dispatch
            sample.allowFollowUp = sample.daysSinceDispatch > 7
          } else {
            sample.daysSinceDispatch = 0
            sample.allowFollowUp = false
          }
        })
        // Sort by days since dispatch (oldest dispatched first)
        filtered.sort((a, b) => (b.daysSinceDispatch || 0) - (a.daysSinceDispatch || 0))
      } else if (this.listFilter === 'dispatched') {
        filtered = regionSamples.filter(
          (sample) => sample.dispatched && (sample.followUpSent === true || sample.followUpSent === undefined)
        )
      } else {
        filtered = regionSamples
      }
      return filtered.filter((sample) => this.matchesSearch(sample))
    }
  }
}
</script>
